import {
  type ComputedRef,
  type MaybeRefOrGetter,
  computed,
  toValue
} from "vue";
import { useStore } from "vuex";

const DEFAULT_PROGRAM_NAME = "Payment Schedule";
const DEFAULT_INTERVAL_NAME = "Installment";

interface ProgressBillingLabelSource {
  program_name?: string | null;
  interval_name?: string | null;
  program_name_plural?: string | null;
  interval_name_plural?: string | null;
}

interface InvoiceWithProgressBilling {
  progress_billing?: ProgressBillingLabelSource | null;
}

interface TenantLabelKeys {
  progress_billing_program_name?: string | null;
  progress_billing_interval_name?: string | null;
  progress_billing_program_name_plural?: string | null;
  progress_billing_interval_name_plural?: string | null;
}

interface ProgressBillingLabels {
  programName: ComputedRef<string>;
  programNamePlural: ComputedRef<string>;
  intervalName: ComputedRef<string>;
  intervalNamePlural: ComputedRef<string>;
}

function pluralize(word: string): string {
  if (word.endsWith("s")) return word;
  return `${word}s`;
}

export function useProgressBillingLabels(
  invoice?: MaybeRefOrGetter<InvoiceWithProgressBilling | null | undefined>
): ProgressBillingLabels {
  let store: any;
  try {
    store = useStore();
  } catch {
    store = undefined;
  }

  const tenantSource = computed<TenantLabelKeys>(() => {
    const featureFlags = store?.state?.tenant?.feature_flags ?? {};
    return featureFlags as TenantLabelKeys;
  });

  const invoiceSource = computed<ProgressBillingLabelSource | undefined>(() => {
    const inv = invoice ? toValue(invoice) : undefined;
    return inv?.progress_billing ?? undefined;
  });

  const programName = computed(
    () =>
      invoiceSource.value?.program_name ||
      tenantSource.value.progress_billing_program_name ||
      DEFAULT_PROGRAM_NAME
  );

  const intervalName = computed(
    () =>
      invoiceSource.value?.interval_name ||
      tenantSource.value.progress_billing_interval_name ||
      DEFAULT_INTERVAL_NAME
  );

  const programNamePlural = computed(
    () =>
      invoiceSource.value?.program_name_plural ||
      tenantSource.value.progress_billing_program_name_plural ||
      pluralize(programName.value)
  );

  const intervalNamePlural = computed(
    () =>
      invoiceSource.value?.interval_name_plural ||
      tenantSource.value.progress_billing_interval_name_plural ||
      pluralize(intervalName.value)
  );

  return { programName, programNamePlural, intervalName, intervalNamePlural };
}
